import { CloudUpload, FilePlus2, FileText, Folder, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate } from '@/features/shared/format'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'
import { BelgeRowActions } from '../components/actions/BelgeRowActions'
import { BelgeYukleModal } from '../components/actions/BelgeYukleModal'
import { BelgeSidePanel } from '../components/BelgeSidePanel'
import { BelgeTakipCard } from '../components/BelgeTakipCard'
import { RequestDocumentModal } from '@/features/hr/belgeler/components/RequestDocumentModal'
import { DOC_FOLLOWUP_PERM } from '@/features/hr/belgeler/followupApi'
import { MobileList } from '../components/MobileList'
import { TABLE_FRAME } from '../components/tableFrame'
import { SectionCard } from '../components/SectionCard'
import type { PersonelDetail } from '../model'
import type { BelgeSatir, Personel } from '../types'
import { HR_WRITE_PERM } from '../../permissions'

function BelgeDurumPill({ durum }: { durum: BelgeSatir['durum'] }) {
  const { t } = useTranslation()
  if (durum === 'klasor') return <span className="text-xs text-muted-foreground">—</span>
  const cls =
    durum === 'tamam'
      ? 'bg-success/12 text-success'
      : durum === 'suresi_doluyor' || durum === 'inceleniyor'
        ? 'bg-warning/15 text-warning-strong'
        : 'bg-destructive/12 text-destructive'
  const dot =
    durum === 'tamam' ? 'bg-success' : durum === 'suresi_doluyor' || durum === 'inceleniyor' ? 'bg-warning' : 'bg-destructive'
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', cls)}>
      <span className={cn('size-1.5 rounded-full', dot)} />
      {t(`hr.personel.belge.durum.${durum}`)}
    </span>
  )
}

function BelgeIcon({ folder }: { folder: boolean }) {
  return folder ? (
    <span className="flex size-8 items-center justify-center rounded-lg bg-warning/15 text-warning">
      <Folder className="size-4 fill-current" />
    </span>
  ) : (
    <span className="flex size-8 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
      <FileText className="size-4" />
    </span>
  )
}

export function BelgelerTab({ personel, belge }: { personel: Personel; belge: PersonelDetail['belge'] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [uploadOpen, setUploadOpen] = useState(false)
  const [requestOpen, setRequestOpen] = useState(false)
  const canFollow = can(DOC_FOLLOWUP_PERM)
  const [q, setQ] = useState('')
  const [kat, setKat] = useState('all')
  const [durum, setDurum] = useState('all')

  const kategoriler = useMemo(
    () => Array.from(new Set(belge.liste.filter((b) => b.kind === 'file').map((b) => b.kategori))).sort(),
    [belge.liste],
  )

  const rows = useMemo(() => {
    const n = q.trim().toLocaleLowerCase('tr')
    return belge.liste.filter((b) => {
      if (kat !== 'all' && b.kategori !== kat && b.klasor !== kat) return false
      if (durum !== 'all') {
        if (b.kind === 'folder') return false
        if (b.durum !== durum) return false
      }
      if (!n) return true
      return (
        b.ad.toLocaleLowerCase('tr').includes(n) ||
        b.kategori.toLocaleLowerCase('tr').includes(n)
      )
    })
  }, [belge.liste, q, kat, durum])

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold tracking-tight">{t('hr.personel.belge.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('hr.personel.belge.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canFollow ? (
            <Button size="sm" variant="outline" onClick={() => setRequestOpen(true)}>
              <FilePlus2 />
              {t('hr.belgeler.request.button')}
            </Button>
          ) : null}
          {can(HR_WRITE_PERM) ? (
            <Button size="sm" className="shadow-sm" onClick={() => setUploadOpen(true)}>
              <CloudUpload />
              {t('hr.personel.belge.yukle')}
            </Button>
          ) : null}
        </div>
      </div>
      {uploadOpen ? <BelgeYukleModal personelId={personel.id} onClose={() => setUploadOpen(false)} /> : null}
      {requestOpen ? <RequestDocumentModal employeeIds={[personel.id]} onDone={() => setRequestOpen(false)} /> : null}

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
        <SectionCard className="overflow-hidden" contentClassName="space-y-0 p-0" noPad>
          <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-10 rounded-xl bg-muted/40 pl-9 shadow-none"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('hr.personel.belge.search')}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Select
                size="lg"
                className="w-full min-w-0 rounded-xl sm:w-auto sm:min-w-[9.5rem]"
                value={kat}
                onChange={setKat}
                options={[
                  { value: 'all', label: t('hr.personel.belge.allKat') },
                  ...kategoriler.map((k) => ({ value: k, label: k })),
                ]}
              />
              <Select
                size="lg"
                className="w-full min-w-0 rounded-xl sm:w-auto sm:min-w-[9.5rem]"
                value={durum}
                onChange={setDurum}
                options={[
                  { value: 'all', label: t('hr.personel.belge.allDurum') },
                  { value: 'tamam', label: t('hr.personel.belge.durum.tamam') },
                  { value: 'suresi_doluyor', label: t('hr.personel.belge.durum.suresi_doluyor') },
                  { value: 'eksik', label: t('hr.personel.belge.durum.eksik') },
                ]}
              />
            </div>
          </div>
          <MobileList
            rows={rows.map((b) => ({
              key: b.id,
              leading: <BelgeIcon folder={b.kind === 'folder'} />,
              title: b.kind === 'folder' ? `${b.ad} · ${t('hr.personel.belge.belgeSay', { n: b.count ?? 0 })}` : b.ad,
              subtitle: b.kategori,
              trailing: (
                <div className="flex items-center gap-1">
                  <BelgeDurumPill durum={b.durum} />
                  {b.kind === 'file' ? <BelgeRowActions personelId={personel.id} personelAd={personel.ad} belge={b} /> : null}
                </div>
              ),
              meta:
                b.kind === 'folder' ? null : (
                  <>
                    <span>{b.tarih === '—' ? '—' : fmtDate(b.tarih)}</span>
                    <span aria-hidden>·</span>
                    <span>{b.gecerlilik === 'Süresiz' ? t('hr.personel.belge.suresiz') : b.gecerlilik}</span>
                  </>
                ),
            }))}
          />
          <Table className="hr-table" wrapperClassName={cn(TABLE_FRAME, 'm-4')}>
            <THead>
              <TR className="hover:bg-transparent">
                <TH className="w-10">
                  <input type="checkbox" aria-label="select-all" className="accent-primary" />
                </TH>
                <TH>{t('hr.personel.belge.ad')}</TH>
                <TH>{t('hr.personel.belge.klasor')}</TH>
                <TH>{t('hr.personel.columns.durum')}</TH>
                <TH>{t('hr.personel.belge.tarih')}</TH>
                <TH>{t('hr.personel.belge.gecerlilik')}</TH>
                <TH className="w-12">{t('hr.personel.columns.islem')}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((b) => (
                <TR key={b.id}>
                  <TD>
                    <input type="checkbox" aria-label={b.ad} className="accent-primary" />
                  </TD>
                  <TD>
                    <div className="flex items-center gap-2.5">
                      <BelgeIcon folder={b.kind === 'folder'} />
                      <div className="min-w-0">
                        <div className="truncate font-semibold">
                          {b.kind === 'folder'
                            ? `${b.ad} · ${t('hr.personel.belge.belgeSay', { n: b.count ?? 0 })}`
                            : b.ad}
                        </div>
                      </div>
                    </div>
                  </TD>
                  <TD className="text-muted-foreground">{b.kategori}</TD>
                  <TD>
                    <BelgeDurumPill durum={b.durum} />
                  </TD>
                  <TD className="whitespace-nowrap text-muted-foreground">
                    {b.tarih === '—' ? '—' : fmtDate(b.tarih)}
                  </TD>
                  <TD className="whitespace-nowrap text-muted-foreground">
                    {b.gecerlilik === 'Süresiz' ? t('hr.personel.belge.suresiz') : b.gecerlilik}
                  </TD>
                  <TD>
                    {b.kind === 'file' ? <BelgeRowActions personelId={personel.id} personelAd={personel.ad} belge={b} /> : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <div className="border-t bg-muted/20 px-5 py-3.5 text-xs text-muted-foreground">
            {t('hr.personel.belge.listeSay', { n: rows.length })}
          </div>
        </SectionCard>

        <div className="space-y-5">
          <BelgeSidePanel belge={belge} />
          {canFollow ? <BelgeTakipCard personelId={personel.id} eksik={belge.ozet.eksik} /> : null}
        </div>
      </div>
    </div>
  )
}
